import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { getCable, handleCableDisconnect, reportRejectedSubscription } from "services/cable";

const CONNECT_WARNING_MS = 15000;

export default function useChannel(channelParams, handlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const subscriptionRef = useRef(null);
  const key = channelParams ? JSON.stringify(channelParams) : null;

  useEffect(() => {
    if (!key) return undefined;

    const cable = getCable();
    if (!cable) return undefined;

    const params = JSON.parse(key);
    let hasConnected = false;

    const connectWarningTimer = setTimeout(() => {
      if (hasConnected) return;
      console.warn(
        `[cable] no connection ${CONNECT_WARNING_MS / 1000}s after subscribing — check REACT_APP_CABLE_URL, the /cable mount path and allowed_request_origins`,
        params
      );
      Sentry.addBreadcrumb({
        category: "cable",
        message: "subscription never connected",
        level: "warning",
        data: params,
      });
    }, CONNECT_WARNING_MS);

    const subscription = cable.subscriptions.create(params, {
      connected() {
        hasConnected = true;
        // `this` is the subscription — ActionCable applies callbacks on it — so
        // perform() is always available and always post-confirmation.
        handlersRef.current?.connected?.(this);
      },
      disconnected(payload) {
        handleCableDisconnect(payload);
        handlersRef.current?.disconnected?.(payload);
      },
      rejected() {
        reportRejectedSubscription(params);
        handlersRef.current?.rejected?.();
      },
      received(data) {
        handlersRef.current?.received?.(data);
      },
    });

    subscriptionRef.current = subscription;

    return () => {
      clearTimeout(connectWarningTimer);
      subscriptionRef.current = null;
      try {
        subscription.unsubscribe();
      } catch (e) {
        // Consumer already torn down (logout).
      }
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return subscriptionRef;
}
